import { fetchSquadFondsdatenDynamicValues } from './fetchSquadFondsdatenDynamic';
import { fetchSquadGebuehrenValues } from './fetchSquadGebuehren';
import { fetchSquadPerformanceRiskValues } from './fetchSquadPerformanceRisk';
import { fetchSquadSectionHeaderDynamicValues } from './fetchSquadSectionHeadersDynamic';
import { resolvePerformanceRiskTable } from './performanceRiskTable';
import { listMissingSquadDynamicValues } from './squadDynamicMissing';
import { resolveSquadGebuehren } from './squadGebuehrenDynamic';
import { fetchSquadSrriwert, resolveSquadFundIsin } from './squadSrriwert';

export { squadMissingDynamicWarning } from './squadDynamicMissing';

export type SquadDynamicBundle = {
  /** Fund ISIN and as-of date the values were fetched for. */
  isin: string;
  until?: string;
  /** SRRI 1–7, or null when the data has none. */
  srri: number | null;
  fondsdatenCache: Map<string, string>;
  sectionHeaderCache: Map<string, string>;
  /** Wertentwicklung/Risikokennzahlen with every dynamic cell resolved — replaces the stored table for rendering. */
  performanceRiskTable: ReturnType<typeof resolvePerformanceRiskTable>;
  /** Gebühren with every dynamic row resolved to its wording — replaces the stored rows for rendering. */
  weitereDaten: Record<string, string>;
  /** Labels of dynamic values the data had nothing for — see squadDynamicMissing. */
  missing: string[];
};

/**
 * Every dynamic SQUAD value the PDF needs, fetched in one place. The PDF dialog and the
 * publish path (`PDFViewerV2/index.tsx`) both call this, so a new source reaches both at
 * once. Before, each fetch was wired into each renderer by hand — and publishing never
 * fetched the SRRI, it printed whatever level had been stored when the form was opened.
 */
export async function fetchSquadDynamicBundle(data: any, template?: any): Promise<SquadDynamicBundle> {
  const isin = resolveSquadFundIsin(data, template);
  const until = data?.published_at || data?.historyUntil || undefined;
  const [srri, fondsdatenCache, sectionHeaderCache, riskTableValues, gebuehrenValues] = await Promise.all([
    fetchSquadSrriwert(isin, until),
    fetchSquadFondsdatenDynamicValues(data?.fondsdaten, isin, until),
    fetchSquadSectionHeaderDynamicValues(data?.sectionHeaders, isin, until),
    fetchSquadPerformanceRiskValues(data?.performanceRiskTable, isin, until),
    fetchSquadGebuehrenValues(data?.weitereDaten, isin, until),
  ]);
  return {
    isin,
    until,
    srri,
    fondsdatenCache,
    sectionHeaderCache,
    performanceRiskTable: resolvePerformanceRiskTable(data?.performanceRiskTable, riskTableValues),
    weitereDaten: resolveSquadGebuehren(data?.weitereDaten, gebuehrenValues),
    missing: listMissingSquadDynamicValues(data, fondsdatenCache, srri, riskTableValues, gebuehrenValues),
  };
}
